/**
 * enfernoHealthWorkouts.js — JS wrapper around the custom native plugin that
 * writes workouts INTO Apple HealthKit (iOS) / Health Connect (Android).
 *
 * Why a custom plugin: @capgo/capacitor-health@8.5.2 supports reads and
 * metric writes but does NOT expose a saveWorkout method. We bridge to
 * HKHealthStore.save(HKWorkout) on iOS and HealthConnectClient.insertRecords
 * (ExerciseSessionRecord) on Android via a small plugin embedded in the app.
 *
 * The plugin is registered native-side as 'EnfernoHealthWorkouts' in:
 *   - ios/App/App/EnfernoHealthWorkoutsPlugin.swift (registered in AppViewController.capacitorDidLoad)
 *   - android/app/src/main/java/.../EnfernoHealthWorkoutsPlugin.kt (registered in MainActivity)
 *
 * On web, the plugin is unavailable; saveWorkoutToHealth() is a no-op that
 * returns `{ saved: false, reason: 'unavailable' }`.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

// registerPlugin gracefully no-ops on web — we still need to guard manually
// before invoking saveWorkout so the UI can show "available on mobile" copy.
const EnfernoHealthWorkouts = registerPlugin('EnfernoHealthWorkouts');

/**
 * Activity-type strings the native plugin understands. Adding a new label
 * here only requires extending the JS map → an existing or near enum value.
 * The native side maps strings to HKWorkoutActivityType / ExerciseType enums.
 */
const VALID_ACTIVITY_TYPES = new Set([
  'running', 'walking', 'cycling',
  'boxing', 'kickboxing', 'bjj', 'martial_arts',
  'strength', 'strength_training', 'calisthenics',
  'other',
]);

const normalizeActivityType = (label) => {
  if (!label) return 'strength';
  const lower = String(label).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return VALID_ACTIVITY_TYPES.has(lower) ? lower : 'other';
};

export const isHealthWriteAvailable = () => isNative;

/**
 * Save an Enferno workout into the native health platform.
 *
 * @param {object} opts
 * @param {string} opts.startDate - ISO 8601
 * @param {string} opts.endDate - ISO 8601
 * @param {string} opts.activityType - e.g. 'strength', 'bjj', 'running'
 * @param {number} [opts.caloriesKcal]
 * @param {number} [opts.distanceMeters]
 * @param {object} [opts.metadata]
 * @returns {Promise<{ saved: boolean, platform?: string, reason?: string }>}
 */
export const saveWorkoutToHealth = async (opts) => {
  if (!isNative) return { saved: false, reason: 'unavailable' };
  try {
    const result = await EnfernoHealthWorkouts.saveWorkout({
      startDate: opts.startDate,
      endDate: opts.endDate,
      activityType: normalizeActivityType(opts.activityType),
      caloriesKcal: opts.caloriesKcal,
      distanceMeters: opts.distanceMeters,
      metadata: { source: 'Enferno', ...(opts.metadata || {}) },
    });
    return { saved: true, ...result };
  } catch (e) {
    return { saved: false, reason: e?.message || 'plugin-error' };
  }
};
